import { type DefaultContext, gqlPromise } from '@egw/clients'
import {
  type CustomerLegalDocumentsInput,
  type CustomerStatementsInput,
  type CustomerTaxDocumentsInput,
  type CustomerStatementsOutput,
  type CustomerStatementsDatesOutput,
  type CustomerTaxDocumentsYearsOutput,
  type CustomerTaxDocumentsOutput,
  type CustomerLegalDocumentPartnersOutput,
  type CustomerLegalDocumentsOutput,
  type InvestmentTradeConfirmLinkInput,
  type InvestmentTradeConfirmLinkOutput,
  type InvestmentTradeConfirmAccountsOutput,
} from '../../models'

/**
 * Get Customer Statements Dates
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CustomerStatementsDates>
 * @example const customerDetails = await getCustomerStatementsDates();
 */
export const getCustomerStatementsDates = async (context?: DefaultContext): Promise<CustomerStatementsDatesOutput> => {
  const response = (await gqlPromise(
    {
      name: 'customerStatementsDates',
      query: `
        query customerStatementsDates {
          customerStatementsDates {
            statementsDates {
              year
              months
            }
          }
        }`,
    },
    context,
  )) as { customerStatementsDates: CustomerStatementsDatesOutput }
  return response.customerStatementsDates
}

/**
 * Get customer Statements
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CustomerStatements>
 * @example const customerDetails = await getCustomerStatements();
 */
export const getCustomerStatements = async (
  customerStatementsInput: CustomerStatementsInput,
  context?: DefaultContext,
): Promise<CustomerStatementsOutput> => {
  const response = (await gqlPromise(
    {
      name: 'customerStatements',
      query: `
        query customerStatements($customerStatementsInput: CustomerStatementsInput!) {
          customerStatements(customerStatementsInput: $customerStatementsInput) {
            statements {
              name
              url
              rank
            }
          }
        }`,
    },
    { customerStatementsInput },
    context,
  )) as { customerStatements: CustomerStatementsOutput }
  return response.customerStatements
}

/**
 * Get Tax Doc Years
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CustomerTaxDocumentsDates>
 * @example const customerDetails = await getTaxDocYearsDocument();
 */
export const getTaxDocYearsDocument = async (context?: DefaultContext): Promise<CustomerTaxDocumentsYearsOutput> => {
  const response = (await gqlPromise(
    {
      name: 'customerTaxDocumentsDates',
      query: `
        query customerTaxDocumentsDates {
          customerTaxDocumentsDates {
            years
          }
        }`,
    },
    context,
  )) as { customerTaxDocumentsDates: CustomerTaxDocumentsYearsOutput }
  return response.customerTaxDocumentsDates
}

/**
 * Get customer Statements
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CustomerStatements>
 * @example const customerDetails = await getCustomerTaxDocuments();
 */
export const getCustomerTaxDocuments = async (
  customerTaxDocumentsInput: CustomerTaxDocumentsInput,
  context?: DefaultContext,
): Promise<CustomerTaxDocumentsOutput> => {
  const response = (await gqlPromise(
    {
      name: 'customerTaxDocuments',
      query: `
        query customerTaxDocuments($customerTaxDocumentsInput: CustomerTaxDocumentsInput!) {
          customerTaxDocuments(customerTaxDocumentsInput: $customerTaxDocumentsInput) {
            documents {
              name
              url
              rank
            }
          }
        }`,
    },
    { customerTaxDocumentsInput },
    context,
  )) as { customerTaxDocuments: CustomerTaxDocumentsOutput }
  return response.customerTaxDocuments
}

/**
 * Get Customer Legal Document Partners
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<CustomerLegalDocumentPartners>
 * @example const customerDetails = await getCustomerLegalDocumentPartners();
 */
export const getCustomerLegalDocumentPartners = async (
  context?: DefaultContext,
): Promise<CustomerLegalDocumentPartnersOutput> => {
  const response = (await gqlPromise(
    {
      name: 'customerLegalDocumentPartners',
      query: `
        query customerLegalDocumentPartners {
          customerLegalDocumentPartners {
            partners
          }
        }`,
    },
    context,
  )) as { customerLegalDocumentPartners: CustomerLegalDocumentPartnersOutput }
  return response.customerLegalDocumentPartners
}

/**
 * Get investment trade confirms
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTradeConfirms>
 * @example const customerDetails = await getInvestmentTradeConfirms();
 */
export const getCustomerLegalDocuments = async (
  customerLegalDocumentsInput: CustomerLegalDocumentsInput,
  context?: DefaultContext,
): Promise<CustomerLegalDocumentsOutput> => {
  const response = (await gqlPromise(
    {
      name: 'customerLegalDocuments',
      query: `
        query customerLegalDocuments($customerLegalDocumentsInput: CustomerLegalDocumentsInput!) {
          customerLegalDocuments(customerLegalDocumentsInput: $customerLegalDocumentsInput) {
            documents {
              name
              url
              rank
            }
          }
        }`,
    },
    { customerLegalDocumentsInput },
    context,
  )) as { customerLegalDocuments: CustomerLegalDocumentsOutput }
  return response.customerLegalDocuments
}

/**
 * Get investment trade confirms link
 */
export const getInvestmentTradeConfirmLink = async (
  investmentTradeConfirmLinkInput: InvestmentTradeConfirmLinkInput,
  context?: DefaultContext,
): Promise<InvestmentTradeConfirmLinkOutput> => {
  const response = (await gqlPromise(
    {
      name: 'investmentTradeConfirmLink',
      query: `
        query investmentTradeConfirmLink($investmentTradeConfirmLinkInput: InvestmentTradeConfirmLinkInput!) {
          investmentTradeConfirmLink(investmentTradeConfirmLinkInput: $investmentTradeConfirmLinkInput) {
            confirm {
              url
            }
          }
        }`,
    },
    { investmentTradeConfirmLinkInput },
    context,
  )) as { investmentTradeConfirmLink: InvestmentTradeConfirmLinkOutput }
  return response.investmentTradeConfirmLink
}

/**
 * Get investment trade confirm accounts (includes CLOSED accounts unlike wealthOverview)
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<InvestmentTradeConfirmAccountsOutput>
 * @example const accounts = await getInvestmentTradeConfirmAccounts();
 */
export const getInvestmentTradeConfirmAccounts = async (
  context?: DefaultContext,
): Promise<InvestmentTradeConfirmAccountsOutput> => {
  const response = (await gqlPromise(
    {
      name: 'investmentTradeConfirmAccounts',
      query: `
        query investmentTradeConfirmAccounts {
          investmentTradeConfirmAccounts {
            accounts {
              alias
              status
              accountName
              __typename
            }
          }
        }`,
    },
    context,
  )) as { investmentTradeConfirmAccounts: InvestmentTradeConfirmAccountsOutput }
  return response.investmentTradeConfirmAccounts
}
